import classNames from "classnames";
import React, { memo, useEffect, useId, useMemo, useRef } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { TextSizeValue } from "../../theme/typography";
import { DisabledTooltip } from "../../tooltip/disabled-tooltip";
import * as toggleCss from "./redo-toggle.module.css";

export const redoToggleSize = ["xs", "sm", "md"] as const;
export type RedoToggleSize = (typeof redoToggleSize)[number];

export const redoToggleLayout = ["inline", "trailing"] as const;
export type RedoToggleLayout = (typeof redoToggleLayout)[number];

export interface RedoToggleProps extends React.HTMLAttributes<HTMLDivElement> {
  "data-dd-action-name"?: string;
  value: boolean;
  setValue(value: boolean): void;
  size?: RedoToggleSize;
  label?: string;
  description?: string;
  disabled?: string | boolean;
  /** Places descriptors on the left and the switch at the far right. */
  layout?: RedoToggleLayout;
  /** Applies settings-page label and helper-text typography. */
  settingsStyle?: boolean;
  labelPosition?: "left" | "right";
  testId?: string;
  semantic?: boolean;
  indeterminate?: boolean;
}

export const RedoToggle = memo(function RedoToggle({
  value,
  setValue,
  size = "xs",
  label,
  description,
  disabled,
  layout = "inline",
  settingsStyle = false,
  labelPosition = "right",
  testId,
  semantic = false,
  indeterminate = false,
  ...rest
}: RedoToggleProps) {
  const inputId = `redo-toggle-${useId()}`;
  const showDescriptors = !!(label || description);
  const resolvedLayout = settingsStyle ? layout : "inline";

  const labelTextColor =
    (disabled && "disabled") ||
    (settingsStyle && "primary") ||
    (value && "primary") ||
    "secondary";
  const descriptionTextColor =
    (disabled && "disabled") || (settingsStyle && "tertiary") || "secondary";

  const onlyLabel = !description && !!label;
  const isTrailing = resolvedLayout === "trailing" && showDescriptors;

  const descriptors = useMemo(
    () =>
      showDescriptors && (
        <Flex dir="column" gap="none">
          {label && (
            <Text
              className={toggleCss.text}
              fontSize={settingsStyle ? "sm" : toggleSizeToLabelSize[size]}
              fontWeight={settingsStyle || value ? "medium" : "regular"}
              textAlign={labelPosition === "left" ? "right" : "left"}
              textColor={labelTextColor}
            >
              {label}
            </Text>
          )}
          {description && (
            <Text
              className={toggleCss.text}
              fontSize={
                settingsStyle ? "xs" : toggleSizeToDescriptionSize[size]
              }
              fontWeight="regular"
              textAlign={labelPosition === "left" ? "right" : "left"}
              textColor={descriptionTextColor}
            >
              {description}
            </Text>
          )}
        </Flex>
      ),
    [
      showDescriptors,
      label,
      description,
      size,
      settingsStyle,
      value,
      labelTextColor,
      descriptionTextColor,
      labelPosition,
    ],
  );

  return (
    <DisabledTooltip
      disabledMessage={disabled}
      otherTooltipProps={
        settingsStyle ? { leftAlign: layout === "trailing" } : undefined
      }
    >
      <Flex
        align={onlyLabel ? "center" : "stretch"}
        as="label"
        data-arbiter="RedoToggle"
        data-testid={testId}
        gap={settingsStyle ? (isTrailing ? "xl" : "md") : undefined}
        justify={
          settingsStyle
            ? isTrailing
              ? "space-between"
              : "flex-start"
            : undefined
        }
        labelProps={{ htmlFor: inputId }}
        w={isTrailing ? "full" : undefined}
        {...rest}
      >
        {(isTrailing || labelPosition === "left") && descriptors}
        <Flex pt={showDescriptors ? "xxs" : "none"}>
          <ToggleSwitch
            disabled={disabled}
            htmlId={inputId}
            indeterminate={indeterminate}
            semantic={semantic}
            setValue={setValue}
            size={size}
            value={value}
          />
        </Flex>
        {!isTrailing && labelPosition === "right" && descriptors}
      </Flex>
    </DisabledTooltip>
  );
});

export const ToggleSwitch = memo(function ToggleSwitch({
  value,
  setValue,
  disabled,
  size,
  semantic = false,
  indeterminate = false,
  htmlId,
}: {
  value: boolean;
  disabled: string | boolean | undefined;
  setValue: (value: boolean) => void;
  size: RedoToggleSize;
  semantic?: boolean;
  indeterminate?: boolean;
  htmlId?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <span className={toggleCss.toggleControl}>
      <input
        aria-checked={indeterminate ? "mixed" : value}
        checked={value}
        className={classNames(toggleCss.toggleInput)}
        disabled={!!disabled}
        id={htmlId}
        onChange={(e) => setValue(e.target.checked)}
        ref={inputRef}
        type="checkbox"
      />
      <div
        className={classNames(
          toggleCss.toggleSwitch,
          toggleSizeToClass[size],
          semantic && toggleCss.semantic,
          indeterminate && toggleCss.indeterminate,
        )}
      />
    </span>
  );
});

const toggleSizeToClass: Record<RedoToggleSize, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  xs: toggleCss.xs,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  sm: toggleCss.sm,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  md: toggleCss.md,
};
const toggleSizeToLabelSize: Record<RedoToggleSize, TextSizeValue> = {
  xs: "xs",
  sm: "sm",
  md: "md",
};

const toggleSizeToDescriptionSize: Record<RedoToggleSize, TextSizeValue> = {
  xs: "xxs",
  sm: "xs",
  md: "sm",
};
